/**
 * GDPR Art.30 Record of Processing Activities (RoPA) generator (Atlas 3.0 §L3.4).
 *
 * Produces a structured RoPA from the manifest. Each processing activity
 * corresponds to a PII-carrying entity or domain cluster. Output formats:
 *   - Structured `RopaRecord[]` (for the Compliance HQ table)
 *   - Markdown string (for download / legal review)
 *   - JSON (for machine consumption / API export)
 */
import type { AtlasManifest, Entity, LegalBasis } from '@atlas-manifest/types';
import { entitiesInScope } from './compliance.js';
import { THIRD_PARTY_REGISTER } from './compliance.js';

// ─── RoPA record types ────────────────────────────────────────────────────────

export interface DataSubjectCategory {
  label: string;
  description: string;
}

export interface DataCategory {
  label: string;
  examples: string[];
  isSpecialCategory: boolean;
}

export interface Recipient {
  name: string;
  role: 'processor' | 'controller' | 'sub-processor' | 'internal';
  country?: string;
  safeguard?: string;
}

export interface RopaRecord {
  /** Activity identifier, e.g. 'User Account Management'. */
  activityId: string;
  /** Plain-language name. */
  activityName: string;
  /** Controller details. */
  controller: string;
  /** Internal department / team responsible. */
  department: string;
  /** Domain this activity belongs to. */
  domain: string;
  /** Entity ids that make up this processing activity. */
  entityIds: string[];
  /** Purpose(s) of processing. */
  purposes: string[];
  /** GDPR Art.6 lawful basis. */
  legalBasis: LegalBasis | null;
  /** Supplementary Art.9 basis for special categories, if applicable. */
  specialCategoryBasis?: string;
  /** Categories of data subjects. */
  dataSubjects: DataSubjectCategory[];
  /** Categories of personal data processed. */
  dataCategories: DataCategory[];
  /** Recipients (internal + third parties). */
  recipients: Recipient[];
  /** ISO 3166-1 third-country transfers. */
  thirdCountryTransfers: string[];
  /** Retention period. */
  retentionPeriod: string;
  /** Technical and organizational security measures. */
  securityMeasures: string[];
  /** Whether a DPIA is required. */
  dpiaRequired: boolean;
  /** DPIA reference if conducted. */
  dpiaRef?: string;
}

// ─── Controller / security defaults ──────────────────────────────────────────

const CONTROLLER = 'Brisarx Inc.';
const DEFAULT_SECURITY_MEASURES = [
  'AES-256 encryption at rest (Firestore + Cloud Storage)',
  'TLS 1.3 encryption in transit',
  'Role-based access control (Firebase Auth + custom claims)',
  'Firebase App Check for client integrity',
  'Audit logging to Firestore /auditLogs collection',
  'HMAC-signed inter-tab BroadcastChannel messages',
  'PII scrubber middleware before AI provider calls',
  'SHA-256 hashed IPs in rate-limiter logs',
];

// ─── Domain → processing activity map ────────────────────────────────────────

interface ActivityTemplate {
  activityName: string;
  department: string;
  purposes: string[];
  dataSubjects: DataSubjectCategory[];
  thirdCountryTransfers: string[];
  retentionPeriod: string;
  dpiaRequired: boolean;
}

const DOMAIN_TEMPLATES: Readonly<Record<string, ActivityTemplate>> = {
  auth: {
    activityName: 'User Authentication & Identity Management',
    department: 'Platform Engineering',
    purposes: [
      'Authenticate users and maintain session integrity',
      'Prevent unauthorized access and account takeover',
      'Support progressive identity (phone-first with optional email linking)',
    ],
    dataSubjects: [{ label: 'Registered users', description: 'Adults and minors aged 13+' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: 'Duration of account + 30 days post-deletion',
    dpiaRequired: false,
  },
  accounts: {
    activityName: 'Financial Account Aggregation',
    department: 'FinData Engineering',
    purposes: [
      'Aggregate connected financial accounts via Plaid open-banking API',
      'Display account balances, transactions, and holdings',
      'Support CFPB 1033 data portability rights',
    ],
    dataSubjects: [{ label: 'Account holders', description: 'Users who have connected bank/investment accounts' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: 'Duration of account or until connection revoked',
    dpiaRequired: true,
  },
  transactions: {
    activityName: 'Transaction Processing & Categorization',
    department: 'FinData Engineering',
    purposes: [
      'Record and categorize income and expense transactions',
      'Power budgeting, goal-tracking, and spending insights',
    ],
    dataSubjects: [{ label: 'Account holders', description: 'Users with connected accounts' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: '7 years (tax compliance) then deleted with account',
    dpiaRequired: false,
  },
  goals: {
    activityName: 'Financial Goal Planning',
    department: 'Product Engineering',
    purposes: ['Help users set, track, and achieve financial goals'],
    dataSubjects: [{ label: 'Registered users', description: 'Adults and minors 13+' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: 'Duration of account',
    dpiaRequired: false,
  },
  ai: {
    activityName: 'AI-Powered Financial Insights & Personalization',
    department: 'AI Engineering',
    purposes: [
      'Generate personalized spending insights using Gemini AI',
      'Power the AI Copilot Q&A and Atlas Ask-Atlas features',
      'Build and maintain AI memory context for better recommendations',
    ],
    dataSubjects: [{ label: 'Consenting users', description: 'Users with AI features enabled' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: 'AI memory: 90 days rolling. Session context: 4 hours',
    dpiaRequired: true,
  },
  investments: {
    activityName: 'Investment Portfolio Tracking',
    department: 'FinData Engineering',
    purposes: ['Track investment holdings, performance, and allocation across portfolios'],
    dataSubjects: [{ label: 'Investor users', description: 'Users with connected investment accounts or manual holdings' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: 'Duration of account',
    dpiaRequired: false,
  },
  billing: {
    activityName: 'Subscription Billing & Payment Processing',
    department: 'Platform Engineering',
    purposes: [
      'Process subscription payments via Stripe',
      'Manage subscription tiers (free / pro) and add-on packs',
    ],
    dataSubjects: [{ label: 'Paying subscribers', description: 'Users on paid plans' }],
    thirdCountryTransfers: ['US'],
    retentionPeriod: '7 years (financial records)',
    dpiaRequired: false,
  },
};

const DEFAULT_TEMPLATE: ActivityTemplate = {
  activityName: 'Internal Data Processing',
  department: 'Engineering',
  purposes: ['Support core product functionality'],
  dataSubjects: [{ label: 'Registered users', description: 'Active Brisarx users' }],
  thirdCountryTransfers: ['US'],
  retentionPeriod: 'Duration of account',
  dpiaRequired: false,
};

// ─── Data category derivation ─────────────────────────────────────────────────

function deriveDataCategories(entities: Entity[]): DataCategory[] {
  const categories: DataCategory[] = [];
  const seen = new Set<string>();

  for (const entity of entities) {
    if ((entity.piiCategory === 'DIRECT' || entity.tags.some((t) => t.startsWith('pii:'))) && !seen.has('contact')) {
      seen.add('contact');
      categories.push({
        label: 'Contact & Identity Data',
        examples: ['email address', 'phone number', 'full name', 'date of birth'],
        isSpecialCategory: false,
      });
    }
    if (entity.piiCategory === 'FINANCIAL' && !seen.has('financial')) {
      seen.add('financial');
      categories.push({
        label: 'Financial Data',
        examples: ['bank account numbers', 'transaction history', 'account balances', 'investment holdings'],
        isSpecialCategory: false,
      });
    }
    if (entity.piiCategory === 'BEHAVIORAL' && !seen.has('behavioral')) {
      seen.add('behavioral');
      categories.push({
        label: 'Behavioral & Inferred Data',
        examples: ['spending patterns', 'AI memory context', 'engagement scores'],
        isSpecialCategory: false,
      });
    }
    if ((entity.piiCategory === 'MINOR' || entity.isMinorVisible) && !seen.has('minor')) {
      seen.add('minor');
      categories.push({
        label: 'Minor-Related Data',
        examples: ['age group', 'parental consent token', 'family group membership'],
        isSpecialCategory: true,
      });
    }
    if (entity.piiCategory === 'QUASI' && !seen.has('technical')) {
      seen.add('technical');
      categories.push({
        label: 'Technical Identifiers',
        examples: ['device fingerprint', 'hashed IP address', 'location region'],
        isSpecialCategory: false,
      });
    }
  }

  return categories;
}

// ─── Recipients derivation ────────────────────────────────────────────────────

function deriveRecipients(domain: string): Recipient[] {
  const recipients: Recipient[] = [
    { name: 'Brisarx Engineering', role: 'internal', country: 'US' },
    { name: 'Google Firebase', role: 'processor', country: 'US', safeguard: 'Standard Contractual Clauses' },
  ];

  const thirdParty = THIRD_PARTY_REGISTER.filter((tp) => tp.domains.includes(domain));
  for (const tp of thirdParty) {
    if (!recipients.some((r) => r.name === tp.name)) {
      recipients.push({
        name: tp.name,
        role: tp.role,
        country: tp.country,
        safeguard: tp.transferMechanism,
      });
    }
  }

  return recipients;
}

// ─── Generator ────────────────────────────────────────────────────────────────

export function generateRopa(manifest: AtlasManifest): RopaRecord[] {
  const gdprEntities = entitiesInScope(manifest, 'gdpr');
  const byDomain = new Map<string, Entity[]>();

  for (const entity of gdprEntities) {
    const existing = byDomain.get(entity.domain) ?? [];
    existing.push(entity);
    byDomain.set(entity.domain, existing);
  }

  const records: RopaRecord[] = [];

  for (const [domain, entities] of byDomain) {
    const template = DOMAIN_TEMPLATES[domain] ?? DEFAULT_TEMPLATE;

    // Determine dominant legal basis from entities
    const bases = entities.map((e) => e.legalBasis).filter(Boolean) as LegalBasis[];
    const basisCounts = new Map<LegalBasis, number>();
    for (const b of bases) {
      basisCounts.set(b, (basisCounts.get(b) ?? 0) + 1);
    }
    let dominantBasis: LegalBasis | null = null;
    let maxCount = 0;
    for (const [b, count] of basisCounts) {
      if (count > maxCount) {
        maxCount = count;
        dominantBasis = b;
      }
    }

    const dataCategories = deriveDataCategories(entities);
    const hasMinors = dataCategories.some((c) => c.isSpecialCategory);

    records.push({
      activityId: domain,
      activityName: template.activityName,
      controller: CONTROLLER,
      department: template.department,
      domain,
      entityIds: entities.map((e) => e.id),
      purposes: template.purposes,
      legalBasis: dominantBasis,
      ...(hasMinors ? { specialCategoryBasis: 'GDPR Art.9(2)(g) — substantial public interest, child protection' } : {}),
      dataSubjects: template.dataSubjects,
      dataCategories,
      recipients: deriveRecipients(domain),
      thirdCountryTransfers: template.thirdCountryTransfers,
      retentionPeriod: template.retentionPeriod,
      securityMeasures: DEFAULT_SECURITY_MEASURES,
      dpiaRequired: template.dpiaRequired || hasMinors,
    });
  }

  return records.sort((a, b) => a.activityId.localeCompare(b.activityId));
}

// ─── Formatters ───────────────────────────────────────────────────────────────

export function formatRopaMarkdown(records: RopaRecord[], manifest: AtlasManifest): string {
  const lines: string[] = [
    '# Record of Processing Activities (RoPA)',
    '## GDPR Art.30 Compliant — Brisarx Inc.',
    '',
    `**Generated:** ${new Date().toISOString()}`,
    `**Manifest commit:** ${manifest.generatedFromCommit}`,
    `**Controller:** Brisarx Inc., c/o Legal (legal@brisarx.com)`,
    '',
    '---',
    '',
  ];

  for (const r of records) {
    lines.push(
      `## ${r.activityName}`,
      '',
      `| Field | Value |`,
      `|---|---|`,
      `| Activity ID | \`${r.activityId}\` |`,
      `| Department | ${r.department} |`,
      `| Domain | \`${r.domain}\` |`,
      `| Lawful Basis | ${r.legalBasis ?? '⚠ **Not set**'} |`,
      `| Retention | ${r.retentionPeriod} |`,
      `| DPIA Required | ${r.dpiaRequired ? '✅ Yes' : 'No'} |`,
      '',
      '**Purposes:**',
      ...r.purposes.map((p) => `- ${p}`),
      '',
      '**Data Subjects:**',
      ...r.dataSubjects.map((ds) => `- ${ds.label}: ${ds.description}`),
      '',
      '**Data Categories:**',
      ...r.dataCategories.map((dc) => `- ${dc.label}${dc.isSpecialCategory ? ' _(special category)_' : ''}: ${dc.examples.join(', ')}`),
      '',
      '**Recipients:**',
      ...r.recipients.map(
        (rec) =>
          `- ${rec.name} (${rec.role})${rec.country ? `, ${rec.country}` : ''}${rec.safeguard ? ` — ${rec.safeguard}` : ''}`,
      ),
      '',
      '**Security Measures:**',
      ...r.securityMeasures.map((m) => `- ${m}`),
      '',
      '---',
      '',
    );
  }

  return lines.join('\n');
}

export function formatRopaJson(records: RopaRecord[]): string {
  return JSON.stringify(
    {
      generatedAt: new Date().toISOString(),
      controller: CONTROLLER,
      schemaVersion: 1,
      activities: records,
    },
    null,
    2,
  );
}
